import type { ProfileData, ProfileResponse } from "../hooks/use-fetch-profile";
import { jwtDecode } from "jwt-decode";

type Balance = {
  amount?: string | undefined;
  currencyCode?: string | undefined;
};
type LoyaltyDetails = {
  accountBalance: number | undefined;
  membershipID?: string | undefined;
  membershipTier?: string | undefined;
  tiles?: number | undefined;
};
type TravelBankDetails = {
  balance?: Balance | undefined;
};
type CustomerDetails = {
  contact: ProfileData["profile"]["customerDetails"]["contact"];
  firstName?: string | undefined;
  lastName?: string | undefined;
  loyaltyDetails: LoyaltyDetails;
  profileImage?: string | undefined;
  profileImageType?: string | undefined;
  travelBankDetails?: TravelBankDetails | undefined;
};

type EnhancedUserDataProperties = {
  customerDetails: CustomerDetails;
  isCreditProfile: boolean;
};

export type EnhancedUserData = CustomerDetails & {
  firstName: string;
  fullName: string;
  initials: string;
  isMosaic: boolean;
  lastName: string;
  userImageSrc: string;
  isCreditProfile: boolean;
};

function mapLoyaltyDetails(
  loyaltyDetails?: ProfileData["profile"]["loyaltyDetails"],
): LoyaltyDetails {
  if (!loyaltyDetails) {
    return {
      accountBalance: undefined,
      membershipID: undefined,
      membershipTier: undefined,
      tiles: undefined,
    };
  }
  const loyalty =
    loyaltyDetails.find((detail) => detail.airline === "B6") ??
    loyaltyDetails[0];

  return {
    accountBalance: loyalty?.accountBalance ?? 0,
    membershipID: loyalty?.membershipID,
    membershipTier: loyalty?.membershipTier,
    // No source field for `tiles` in the profile response.
    tiles: undefined,
  };
}

function mapTravelBankDetails(
  travelBankDetails: ProfileData["profile"]["travelBankDetails"] | undefined,
): TravelBankDetails | undefined {
  const balance = travelBankDetails?.balance;

  if (!balance) {
    return undefined;
  }

  return {
    balance: {
      amount: String(balance.amount),
      currencyCode: balance.currency,
    },
  };
}

function enhanceUserData({
  customerDetails,
  isCreditProfile,
}: EnhancedUserDataProperties): EnhancedUserData {
  const firstName = customerDetails.firstName
    ? customerDetails.firstName.charAt(0).toUpperCase() +
      customerDetails.firstName.slice(1).toLocaleLowerCase()
    : "";
  const lastName = customerDetails.lastName
    ? customerDetails.lastName.charAt(0).toUpperCase() +
      customerDetails.lastName.slice(1).toLocaleLowerCase()
    : "";
  const firstInitial = firstName[0] ?? "";
  const lastInitial = lastName[0] ?? "";

  const isMosaic = isCreditProfile
    ? false
    : (customerDetails.loyaltyDetails.membershipTier?.includes("MOSAIC") ??
      false);

  const userImageSource =
    customerDetails.profileImageType && customerDetails.profileImage
      ? `data:${customerDetails.profileImageType};base64,${customerDetails.profileImage}`
      : "";

  return {
    ...customerDetails,
    firstName,
    fullName: firstName ? `${firstName} ${lastName}` : "",
    initials: firstInitial + lastInitial,
    isCreditProfile,
    isMosaic,
    lastName,
    userImageSrc: userImageSource,
  };
}

export function kitAuthMapProfile(
  response: ProfileResponse,
  accessToken: string,
): EnhancedUserData {
  const { profile } = response.data;
  const { customerDetails, loyaltyDetails } = profile;

  return enhanceUserData({
    customerDetails: {
      contact: customerDetails.contact,
      firstName: customerDetails.firstName,
      lastName: customerDetails.lastName,
      loyaltyDetails: mapLoyaltyDetails(loyaltyDetails),
      profileImage: undefined,
      profileImageType: undefined,
      travelBankDetails: mapTravelBankDetails(profile.travelBankDetails),
    },
    isCreditProfile: isCreditProfile(accessToken),
  });
}

const isCreditProfile = (accessToken: string): boolean => {
  try {
    const decodedToken = jwtDecode<{ profileType?: string }>(accessToken);
    return decodedToken.profileType === "JBA";
  } catch {
    return false;
  }
};
